// mRemoteNG connection-file import (`confCons.xml`). An mRemoteNG export is an XML tree of
// `<Node>` elements: `Type="Container"` nodes are folders, `Type="Connection"` nodes are leaf
// connections. Unlike RDM, the hierarchy is the XML nesting itself (not a `\`-delimited path) and
// passwords ARE recoverable: each `Password`/`RDGatewayPassword` is AES-256-GCM encrypted under a
// master password (default `mR3m`) with a PBKDF2-HMAC-SHA1 derived key. We decrypt them in the
// browser and map the tree onto our model exactly like the RDM importer (folders → folder nodes,
// connections → access items), so `ImportDialog.runRdm` can create both unchanged.

import type { Field } from "./items";
import { buildImportTree, fld, type ImportAccess, type ImportNode, type ImportTree } from "./importTree";

// The parsed shape is the shared import-tree model, so it reuses ImportDialog.runRdm verbatim.
export type MrngNode = ImportNode;
export type MrngAccess = ImportAccess;
export type MrngParsed = ImportTree;

const DEFAULT_MASTER_PASSWORD = "mR3m"; // mRemoteNG's built-in default when no custom password is set

/** Thrown when the file is a valid mRemoteNG export but the master password is wrong/needed, so the
 *  UI can prompt for it (mirrors the .kdbx password flow). Distinct from a generic parse failure. */
export class MrngPasswordRequired extends Error {
  constructor() {
    super("mRemoteNG master password required");
    this.name = "MrngPasswordRequired";
  }
}

// --- mRemoteNG password decryption (AES-256-GCM, PBKDF2-HMAC-SHA1) ---------------------------------
// Blob layout: [salt(16)] [nonce(16)] [ciphertext] [GCM tag(16)]. The salt doubles as the GCM
// associated data. Key = PBKDF2(password, salt, iterations, SHA-1) → 32 bytes (AES-256).

const SALT_LEN = 16;
const NONCE_LEN = 16;
const TAG_LEN = 16;

/** A base64 slice as a fresh ArrayBuffer (a clean BufferSource for WebCrypto). */
function b64Slice(s: string): ArrayBuffer {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
const utf8 = (s: string): ArrayBuffer => new TextEncoder().encode(s).slice().buffer;

/** Decrypt one mRemoteNG-encrypted attribute. Returns "" for an empty input. Throws if the GCM tag
 *  fails to authenticate (wrong password) or the blob is malformed. */
async function decryptValue(b64: string, password: string, iterations: number): Promise<string> {
  if (!b64) return "";
  const buf = b64Slice(b64);
  if (buf.byteLength < SALT_LEN + NONCE_LEN + TAG_LEN) throw new Error("encrypted value too short");
  const salt = buf.slice(0, SALT_LEN);
  const nonce = buf.slice(SALT_LEN, SALT_LEN + NONCE_LEN);
  const ctAndTag = buf.slice(SALT_LEN + NONCE_LEN); // WebCrypto expects ciphertext||tag together
  const baseKey = await crypto.subtle.importKey("raw", utf8(password), "PBKDF2", false, ["deriveKey"]);
  const key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-1" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce, additionalData: salt, tagLength: TAG_LEN * 8 }, key, ctAndTag);
  return new TextDecoder().decode(plain);
}

// --- XML helpers ----------------------------------------------------------------------------------

const attr = (el: Element, name: string): string => el.getAttribute(name) ?? "";
// mRemoteNG treats "Yes"/"Always"/"true"/"1" as on (case-insensitive).
const truthy = (v: string): boolean => /^(yes|always|true|1)$/i.test(v.trim());

/** Map an mRemoteNG protocol to a vault item type and build its non-credential fields. Credentials
 *  (decrypted asynchronously) are layered on afterwards by the caller. */
function baseAccess(el: Element): { type: string; fields: Field[] } {
  const protocol = attr(el, "Protocol").toUpperCase();
  const host = attr(el, "Hostname");
  const port = attr(el, "Port");
  switch (protocol) {
    case "RDP":
      return { type: "rdp", fields: [fld("host", "Host", "host", host), fld("port", "Port", "port", port || "3389")] };
    case "SSH1":
    case "SSH2":
    case "SSH":
      return { type: "ssh", fields: [fld("host", "Host", "host", host), fld("port", "Port", "port", port || "22")] };
    case "HTTP":
      return { type: "website", fields: [fld("url", "URL", "url", host ? `http://${host}${port && port !== "80" ? `:${port}` : ""}/` : "")] };
    case "HTTPS":
      return { type: "website", fields: [fld("url", "URL", "url", host ? `https://${host}${port && port !== "443" ? `:${port}` : ""}/` : "")] };
    default:
      // VNC, Telnet, RAW, PowerShell, IntApp, … → generic, keeping host/port so nothing is lost.
      return {
        type: "generic",
        fields: [fld("host", "Host", "host", host), ...(port ? [fld("port", "Port", "port", port)] : [])],
      };
  }
}

// --- Parse ----------------------------------------------------------------------------------------

/** Parse an mRemoteNG `confCons.xml`. Tries the default master password unless one is supplied;
 *  throws {@link MrngPasswordRequired} when a custom password is needed, or a plain Error when the
 *  file isn't a recognizable/supported mRemoteNG export. */
export async function parseMRemoteNG(text: string, password?: string): Promise<MrngParsed> {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("not valid XML");
  const root = doc.documentElement;
  // The root is `<mrng:Connections>`; match the local name regardless of namespace prefix.
  const localName = (el: Element): string => (el.localName || el.nodeName).replace(/^.*:/, "");
  if (!root || localName(root) !== "Connections") throw new Error("not an mRemoteNG export (no <Connections> root)");

  if (truthy(attr(root, "FullFileEncryption"))) {
    throw new Error("fully-encrypted mRemoteNG files are not supported — re-export with 'Full file encryption' off");
  }
  const engine = (attr(root, "EncryptionEngine") || "AES").toUpperCase();
  const mode = (attr(root, "BlockCipherMode") || "GCM").toUpperCase();
  if (engine !== "AES" || mode !== "GCM") {
    throw new Error(`unsupported mRemoteNG encryption (${engine}/${mode}); only AES/GCM is supported`);
  }
  const iterations = parseInt(attr(root, "KdfIterations"), 10) || 1000;

  // Resolve the master password: use the supplied one, else try mRemoteNG's default. The "Protected"
  // attribute is a known-plaintext check value — if it decrypts (GCM authenticates), the password is
  // right. Without it, we fall back to probing the first real password below.
  const pw = password ?? DEFAULT_MASTER_PASSWORD;
  const protectedVal = attr(root, "Protected");
  if (protectedVal) {
    try {
      await decryptValue(protectedVal, pw, iterations);
    } catch {
      throw new MrngPasswordRequired();
    }
  }

  const nodes: MrngNode[] = [];
  const accesses: MrngAccess[] = [];
  // Decrypt a value, surfacing a wrong-password failure as MrngPasswordRequired (covers files that
  // carry no "Protected" check value) and any other failure as an empty value (skip a single field).
  let probed = !!protectedVal;
  const decrypt = async (b64: string): Promise<string> => {
    if (!b64) return "";
    try {
      const v = await decryptValue(b64, pw, iterations);
      probed = true;
      return v;
    } catch {
      if (!probed) throw new MrngPasswordRequired();
      return "";
    }
  };

  // Walk the XML tree, building folder nodes for containers and access items for connections. The
  // path is the `\`-joined ancestry of container names, mirroring the RDM path model.
  async function walk(parent: Element, parentPath: string | null) {
    for (const el of Array.from(parent.children)) {
      if (localName(el) !== "Node") continue;
      const name = attr(el, "Name") || "(unnamed)";
      const type = attr(el, "Type");
      if (type === "Container") {
        const path = parentPath ? `${parentPath}\\${name}` : name;
        nodes.push({ path, name, parentPath, kind: "folder" });
        await walk(el, path);
      } else {
        // A connection (Type="Connection", or anything that isn't a container).
        const { type: itemType, fields } = baseAccess(el);
        const notes = attr(el, "Descr") || undefined;
        const username = attr(el, "Username");
        const domain = attr(el, "Domain");
        const password = await decrypt(attr(el, "Password"));
        // RDP keeps the domain in its own field (matching the catalog + the RDM CSV merge); other
        // types fold a domain into the username as `domain\user`.
        if (itemType === "rdp") {
          if (domain) fields.push(fld("domain", "Domain", "text", domain));
          if (username) fields.push(fld("username", "Username", "username", username));
        } else if (username) {
          fields.push(fld("username", "Username", "username", domain ? `${domain}\\${username}` : username));
        }
        if (password) fields.push(fld("password", "Password", "password", password));

        // RD Gateway (RDP only). Honour it only when actually in use (UsageMethod != "Never").
        if (itemType === "rdp" && !/^never$/i.test(attr(el, "RDGatewayUsageMethod"))) {
          const gwHost = attr(el, "RDGatewayHostname");
          if (gwHost) {
            fields.push(fld("gateway_address", "Gateway address", "host", gwHost));
            const useConn = truthy(attr(el, "RDGatewayUseConnectionCredentials"));
            fields.push(
              fld(
                "gateway_credentials",
                "Gateway credentials",
                "text",
                useConn ? "Use connection credentials" : "Separate gateway credentials",
              ),
            );
            if (!useConn) {
              const gwUser = attr(el, "RDGatewayUsername");
              const gwDomain = attr(el, "RDGatewayDomain");
              const gwPass = await decrypt(attr(el, "RDGatewayPassword"));
              if (gwUser) fields.push(fld("gateway_username", "Gateway username", "username", gwDomain ? `${gwDomain}\\${gwUser}` : gwUser));
              if (gwPass) fields.push(fld("gateway_password", "Gateway password", "password", gwPass));
            }
          }
        }
        accesses.push({ type: itemType, name, notes, fields, groupPath: parentPath ?? "" });
      }
    }
  }
  await walk(root, null);

  // Collapse to the backend depth limit + tally counts (shared with the RDM importer).
  return buildImportTree(nodes, accesses);
}
